/**
 * Runtime public configuration: sync `public/public-config.js` (global), then optional
 * `/runtime-config.json`, then `window.__PUBLIC_RUNTIME_CONFIG__`.
 * GCP: render `public-config.js` and/or `runtime-config.json` from Parameter Manager (non-secret only).
 * Build-time `VITE_*` remains fallback via `environment.ts` after `getPublicRuntimeConfig()`.
 */

export type DeliveryAnalyticsPublicRuntimeConfig = {
  nodeEnv?: string;
  apiBaseUrl?: string;
  apiUrl?: string;
  wsUrl?: string;
  sentryDsn?: string;
  googleMapsApiKey?: string;
  autoRefreshInterval?: number | string;
};

declare global {
  interface Window {
    __DELIVERY_ANALYTICS_PUBLIC_CONFIG__?: Partial<DeliveryAnalyticsPublicRuntimeConfig>;
    __PUBLIC_RUNTIME_CONFIG__?: Partial<DeliveryAnalyticsPublicRuntimeConfig>;
  }
}

let merged: DeliveryAnalyticsPublicRuntimeConfig | null = null;

function fromScript(): DeliveryAnalyticsPublicRuntimeConfig {
  if (typeof window === 'undefined') return {};
  const c = window.__DELIVERY_ANALYTICS_PUBLIC_CONFIG__;
  return c && typeof c === 'object' ? { ...c } : {};
}

function configUrl(): string {
  const base = import.meta.env.BASE_URL || '/';
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return `${prefix}runtime-config.json`;
}

export async function loadRuntimePublicConfig(): Promise<void> {
  const fromSync = fromScript();
  let fromFile: DeliveryAnalyticsPublicRuntimeConfig = {};
  try {
    const res = await fetch(configUrl(), { cache: 'no-store' });
    if (res.ok) {
      fromFile = (await res.json()) as DeliveryAnalyticsPublicRuntimeConfig;
    }
  } catch {
    fromFile = {};
  }
  const injected =
    typeof window !== 'undefined' && window.__PUBLIC_RUNTIME_CONFIG__
      ? window.__PUBLIC_RUNTIME_CONFIG__
      : {};
  merged = { ...fromSync, ...fromFile, ...injected };
}

export function getPublicRuntimeConfig(): DeliveryAnalyticsPublicRuntimeConfig {
  if (merged !== null) return merged;
  return fromScript();
}
